"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useAction, useConvexAuth } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import type { CheckoutShipment } from "@convex/schemas/validators/checkoutShipment";
import { getGuestCheckoutSessionId } from "@/lib/checkout/guest-checkout-session";
import { handleError } from "@/lib/handle-error";
import { track } from "@/lib/analytics/tracker";
import { deliveryFromShipments } from "./use-direct-checkout";

export type CartFreightQuote = FunctionReturnType<
  typeof api.cart.checkout.quoteCartCheckout
>;

export interface CartFreightState {
  quote: CartFreightQuote | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

interface SettledQuote {
  signature: string;
  quote: CartFreightQuote | null;
  error: string | null;
}

type QuoteItem = { productId: Id<"products">; quantity: number };
type QuoteInput = { items: QuoteItem[]; shipments: CheckoutShipment[] };
export type FreightQuoteSource = "cart" | "listing";

const QUOTE_DEBOUNCE_MS = 400;

function useQuoteRequest(
  isAuthenticated: boolean,
  source: FreightQuoteSource,
) {
  const quoteMember = useAction(api.cart.checkout.quoteCartCheckout);
  const quoteItems = useAction(api.cart.checkout.quoteGuestCartCheckout);
  const shown = useRef(false);
  return useCallback(
    ({ items, shipments }: QuoteInput) => {
      const { deliveryWindowEnd: _end, hasDockOnSite: _dock, ...delivery } =
        deliveryFromShipments(shipments);
      const quoted =
        isAuthenticated && source === "cart"
          ? quoteMember({ ...delivery, shipments })
          : quoteItems({
              ...delivery,
              shipments,
              items,
              guestSessionId: getGuestCheckoutSessionId(),
              surface: source,
            });
      return quoted.then((quote) => {
        if (!shown.current) {
          shown.current = true;
          track("checkout.quote_shown", { surface: source });
        }
        return quote;
      });
    },
    [isAuthenticated, source, quoteMember, quoteItems],
  );
}

export function useCartFreightQuote(
  items: QuoteItem[],
  shipments: CheckoutShipment[],
  enabled: boolean,
  source: FreightQuoteSource = "cart",
): CartFreightState {
  const { isAuthenticated } = useConvexAuth();
  const request = useQuoteRequest(isAuthenticated, source);
  const [settled, setSettled] = useState<SettledQuote | null>(null);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const signature = JSON.stringify([isAuthenticated, items, shipments, revision]);
  const active = enabled && shipments.length > 0;
  const latest = useRef<QuoteInput>({ items, shipments });
  useEffect(() => {
    latest.current = { items, shipments };
  }, [items, shipments]);

  useEffect(() => {
    if (!active) return;
    let canceled = false;
    const timer = setTimeout(() => {
      request(latest.current)
        .then((quote) => {
          if (!canceled) setSettled({ signature, quote, error: null });
        })
        .catch((thrown) => {
          if (canceled) return;
          const handled = handleError(thrown, {
            display: "silent",
            fallbackMessage: "Could not quote delivery",
          });
          track("checkout.blocked", {
            stage: "quote",
            surface: source,
            code: handled.code,
            reason: handled.message,
          });
          setSettled({ signature, quote: null, error: handled.message });
        });
    }, QUOTE_DEBOUNCE_MS);
    return () => {
      canceled = true;
      clearTimeout(timer);
    };
  }, [signature, active, request, source]);

  return toFreightState(active, signature, settled, refresh);
}

function toFreightState(
  active: boolean,
  signature: string,
  settled: SettledQuote | null,
  refresh: () => void,
): CartFreightState {
  if (!active) return { quote: null, loading: false, error: null, refresh };
  if (settled?.signature !== signature) {
    return { quote: null, loading: true, error: null, refresh };
  }
  return { ...settled, loading: false, refresh };
}
